import {
  createSlice,
  createEntityAdapter,
  createAsyncThunk,
  combineReducers,
} from '@reduxjs/toolkit';

import {
  getPagedTopics,
  getPagedSharedTopics,
  deleteTopic,
  updateTopicOrder,
  putTopic,
  postTopic,
} from '../../../features/project/topicService';

const moveArrayItem = (array, oldIndex, newIndex) => {
  if (newIndex >= array.length) {
    newIndex = array.length - 1;
  }
  array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
  return array;
};

export const fetchTopicsByPage = createAsyncThunk(
  'projectOverview/fetchTopicsById',
  async ({ projectId, page = 0, pageSize }) => {
    const response = await getPagedTopics(projectId, page, pageSize);
    return response;
  }
);

export const fetchSharedTopicsByPage = createAsyncThunk(
  'projectOverview/fetchTopicsById',
  async ({ projectId, page = 0, pageSize }) => {
    const response = await getPagedSharedTopics(projectId, page, pageSize);
    return response;
  }
);

export const deleteTopicById = createAsyncThunk(
  'projectOverview/deleteTopicById',
  async ({ projectId, topicThreadId }) => {
    const response = await deleteTopic(projectId, topicThreadId);
    return response;
  }
);

export const moveTopicById = createAsyncThunk(
  'projectOverview/moveTopicById',
  async ({ projectId, topicThreadId, moveDirection }) => {
    const response = await updateTopicOrder(
      projectId,
      topicThreadId,
      moveDirection
    );
    return { response, moveDirection, topicThreadId };
  }
);

export const updateTopicById = createAsyncThunk(
  'projectOverview/updateTopicById',
  async ({ projectId, topicThreadId, topicToSaveDto }) => {
    const response = await putTopic(projectId, topicThreadId, topicToSaveDto);
    return { response, topicThreadId, topicToSaveDto };
  }
);

export const createTopic = createAsyncThunk(
  'projectOverview/createTopic',
  async ({ projectId, topicToSaveDto }) => {
    const response = await postTopic(projectId, topicToSaveDto);
    return { response, topicToSaveDto };
  }
);

export const topicsAdapter = createEntityAdapter({
  selectId: (topic) => {
    return topic.content.contentThreadId;
  },
});

export const initialStateTopics = topicsAdapter.getInitialState({
  loading: false,
  isUpdating: false,
  isCreating: false,
});

export const initialStateTopicsDelete = {
  loading: null,
  error: null,
};

const topicSavingInititalState = {};

const topicSavingSlice = createSlice({
  name: 'topicSavingSlice',
  initialState: topicSavingInititalState,
  reducers: {
    setIsSaving: (state, action) => {
      state[action.payload.contentThreadId] = {
        isSaving: true,
        isSavingFulfilled: false,
        isSavingRejected: false,
      };
    },
    setIsSavingFulfilled: (state, action) => {
      state[action.payload.contentThreadId] = {
        isSaving: false,
        isSavingFulfilled: true,
        isSavingRejected: false,
      };
    },
    setIsSavingRejected: (state, action) => {
      state[action.payload.contentThreadId] = {
        isSaving: false,
        isSavingFulfilled: false,
        isSavingRejected: true,
      };
    },
    clearIsSaving: (state, action) => {
      state[action.payload.contentThreadId] = {
        isSaving: false,
        isSavingFulfilled: false,
        isSavingRejected: false,
      };
    },
    resetIsSaving: () => topicSavingInititalState,
  },
});

const projectOverviewTopicsSlice = createSlice({
  name: 'projectOverviewTopicsSlice',
  initialState: initialStateTopics,
  reducers: {
    removeTopic: topicsAdapter.removeOne,
    updateTopic: topicsAdapter.updateOne,
    addTopic: (state, { payload: topic }) => {
      state.ids = [topic.content.contentThreadId, ...state.ids];
      state.entities = {
        ...state.entities,
        [topic.content.contentThreadId]: topic,
      };
    },
    removeAllTopics: topicsAdapter.removeAll,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTopicsByPage.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(fetchTopicsByPage.fulfilled, (state, action) => {
      topicsAdapter.upsertMany(state, action.payload.items);
      state.loading = false;
      state.error = false;
      state.rowCount = action.payload.rowCount;
      state.currentPage = action.payload.currentPage;
    });
    builder.addCase(fetchTopicsByPage.rejected, (state) => {
      state.error = true;
    });
    builder.addCase(moveTopicById.fulfilled, (state, action) => {
      const { moveDirection, topicThreadId } = action.payload;
      const currentIndex = state.ids.findIndex((id) => id === topicThreadId);

      if (moveDirection === 2) {
        state.ids = moveArrayItem(state.ids, currentIndex, currentIndex + 1);
      }

      if (moveDirection === 1) {
        state.ids = moveArrayItem(state.ids, currentIndex, currentIndex - 1);
      }
    });
    builder.addCase(updateTopicById.pending, (state) => {
      state.isUpdating = true;
    });
    builder.addCase(updateTopicById.fulfilled, (state) => {
      state.isUpdating = false;
    });
    builder.addCase(updateTopicById.rejected, (state) => {
      state.isUpdating = false;
    });
    builder.addCase(createTopic.pending, (state) => {
      state.isCreating = true;
    });
    builder.addCase(createTopic.fulfilled, (state) => {
      state.isCreating = false;
    });
    builder.addCase(createTopic.rejected, (state) => {
      state.isCreating = false;
    });
  },
});

const projectOverviewTopicsDeleteSlice = createSlice({
  name: 'projectOverviewTopicsDeleteSlice',
  initialState: initialStateTopicsDelete,
  extraReducers: (builder) => {
    builder.addCase(deleteTopicById.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(deleteTopicById.fulfilled, (state) => {
      state.loading = false;
      state.error = false;
    });
    builder.addCase(deleteTopicById.rejected, (state) => {
      state.error = true;
    });
  },
});

export const {
  selectById: selectTopicById,
  selectIds: selectTopicIds,
  selectEntities: selectTopicEntities,
  selectAll: selectAllTopic,
  selectTotal: selectTotalTopic,
} = topicsAdapter.getSelectors((state) => state.projectOverview.topics);

export const projectOverviewReducer = combineReducers({
  topics: projectOverviewTopicsSlice.reducer,
  delete: projectOverviewTopicsDeleteSlice.reducer,
  saving: topicSavingSlice.reducer,
});

// Actions
export const { removeTopic, updateTopic, addTopic, removeAllTopics } =
  projectOverviewTopicsSlice.actions;

export const {
  setIsSaving,
  setIsSavingFulfilled,
  setIsSavingRejected,
  clearIsSaving,
  resetIsSaving,
} = topicSavingSlice.actions;

export const selectTopicSavingState = (contentThreadId) => (state) =>
  state.projectOverview.saving[contentThreadId] || {};

// Selectors
export const selectTopicsIsError = (state) =>
  state.projectOverview.topics.error;
export const selectTopicsIsLoading = (state) =>
  state.projectOverview.topics.loading;
export const selectTopicsIsCreating = (state) =>
  state.projectOverview.topics.isCreating;
export const selectTopicsIsUpdating = (state) =>
  state.projectOverview.topics.isUpdating;
export const selectTopicsState = (state) => state.projectOverview.topics;
export const selectTopicDeleteIsLoading = (state) =>
  state.projectOverview.delete.loading;
